import React, { useEffect, useState } from 'react';

// Sticky site header. The site previously had no navigation at all, which left
// visitors scrolling ~11 mobile viewports with no map of what was on the page.
//
// `links` are section anchors on the homepage and real routes elsewhere, so the
// same header works on /, /work and /blog.

const NAV_LINKS = [
  { label: 'Work', to: 'work' },
  { label: 'Automations', to: 'automations' },
  { label: 'Case studies', to: 'case-studies' },
  { label: 'Writing', to: 'blog' },
  { label: 'Pricing', to: 'pricing' },
];

export const SiteNav = ({
  onOpenContact,
  onNavigateHome,
  onNavigateBlog,
  onNavigateWork,
  onNavigateCaseStudies,
  onNavigateAutomations,
  variant = 'home',
}) => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close the mobile menu on Escape so keyboard users aren't trapped.
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e) => e.key === 'Escape' && setMenuOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  const go = (target) => {
    setMenuOpen(false);

    if (target === 'blog') {
      onNavigateBlog?.();
      return;
    }
    if (target === 'automations') {
      onNavigateAutomations?.();
      return;
    }
    if (target === 'case-studies') {
      onNavigateCaseStudies?.();
      return;
    }
    if (target === 'work') {
      // On the homepage the featured grid is right there; elsewhere, route to /work.
      if (variant === 'home') {
        document.getElementById('work')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        onNavigateWork?.();
      }
      return;
    }
    if (target === 'pricing') {
      if (variant === 'home') {
        document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        onNavigateHome?.();
        // Wait for the homepage to mount before scrolling to the section.
        window.setTimeout(
          () => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
          80
        );
      }
    }
  };

  return (
    <header
      className={`sticky top-0 z-40 transition-colors duration-300 ${
        scrolled ? 'bg-white/90 backdrop-blur-md border-b border-[#E0EBF0]' : 'bg-transparent border-b border-transparent'
      }`}
      data-testid="site-nav"
    >
      <div className="max-w-[1200px] mx-auto px-6 h-16 flex items-center justify-between gap-6">
        <button
          type="button"
          onClick={() => {
            setMenuOpen(false);
            onNavigateHome?.();
          }}
          className="text-lg font-semibold text-[#051A24] py-2 -my-2 pr-2"
          style={{ fontFamily: "'Playfair Display', serif" }}
          data-testid="nav-logo"
        >
          @brandononchain
        </button>

        {/* Desktop links */}
        <nav className="hidden md:flex items-center gap-1" aria-label="Main">
          {NAV_LINKS.map((link) => (
            <button
              key={link.label}
              type="button"
              onClick={() => go(link.to)}
              className="px-4 py-3 text-sm font-medium text-[#273C46] hover:text-[#051A24] transition-colors rounded-full"
              data-testid={`nav-link-${link.to}`}
            >
              {link.label}
            </button>
          ))}
          <button
            type="button"
            onClick={onOpenContact}
            className="ml-2 px-5 py-3 rounded-full bg-[#051A24] text-white text-sm font-medium hover:bg-[#0D212C] transition-colors"
            data-testid="nav-contact-btn"
          >
            Contact
          </button>
        </nav>

        {/* Mobile toggle */}
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          className="md:hidden w-11 h-11 -mr-2 flex items-center justify-center rounded-full text-[#051A24]"
          aria-expanded={menuOpen}
          aria-controls="mobile-nav"
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          data-testid="nav-mobile-toggle"
        >
          <span className="relative block w-5 h-4" aria-hidden="true">
            <span
              className={`absolute left-0 w-5 h-[2px] bg-current transition-transform duration-200 ${
                menuOpen ? 'top-[7px] rotate-45' : 'top-0'
              }`}
            />
            <span
              className={`absolute left-0 top-[7px] w-5 h-[2px] bg-current transition-opacity duration-200 ${
                menuOpen ? 'opacity-0' : 'opacity-100'
              }`}
            />
            <span
              className={`absolute left-0 w-5 h-[2px] bg-current transition-transform duration-200 ${
                menuOpen ? 'top-[7px] -rotate-45' : 'top-[14px]'
              }`}
            />
          </span>
        </button>
      </div>

      {/* Mobile panel */}
      <div
        id="mobile-nav"
        hidden={!menuOpen}
        className="md:hidden border-t border-[#E0EBF0] bg-white"
        data-testid="nav-mobile-panel"
      >
        <nav className="max-w-[1200px] mx-auto px-6 py-3 flex flex-col" aria-label="Main">
          {NAV_LINKS.map((link) => (
            <button
              key={link.label}
              type="button"
              onClick={() => go(link.to)}
              className="text-left py-3 text-base font-medium text-[#273C46] hover:text-[#051A24] transition-colors"
            >
              {link.label}
            </button>
          ))}
          <button
            type="button"
            onClick={() => {
              setMenuOpen(false);
              onOpenContact?.();
            }}
            className="mt-3 mb-2 py-3 rounded-full bg-[#051A24] text-white text-sm font-medium"
          >
            Contact
          </button>
        </nav>
      </div>
    </header>
  );
};

export default SiteNav;
